/**
 * External integration types.
 * Two classes of integrations:
 *  - "Seat"  — per-user license assignment → writes OC_AppAssignments
 *  - "Usage" — metered cloud/API consumption → writes OC_CloudSpend (new list)
 */

export type IntegrationProvider =
  // Seat-based
  | 'AzureDevOps'
  | 'GitHub'
  | 'Figma'
  | 'Claude'
  | 'Snyk'
  | 'SendGrid'
  | 'Slack'
  | 'Linear'
  | 'Notion'
  | 'Windsurf'
  | 'CSV'
  // Usage-based
  | 'AzureUsage'
  | 'GCPUsage'
  | 'TwilioUsage';

export type IntegrationKind = 'seat' | 'usage';

export interface IntegrationConfig {
  provider: IntegrationProvider;
  enabled: boolean;
  lastSyncedAt?: string;
  apiKey?: string;
  organizationId?: string;
  mappedAppId?: string;
  costPerSeat: number;
  billingCycle: 'Monthly' | 'Annually';
}

export interface IntegrationSyncResult {
  provider: IntegrationProvider;
  appName: string;
  usersFound: number;
  assigned: number;
  revoked: number;
  unmatched: string[];
  error?: string;
}

export interface ExternalUserSeat {
  email: string;
  name: string;
  role?: string;
  seatType?: string;
  licensedAt?: string;
}

export interface CloudSpendRecord {
  provider: 'AzureUsage' | 'GCPUsage' | 'TwilioUsage';
  service: string;
  subscriptionId: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  currency: string;
  category: string;
}

export interface IntegrationProviderDef {
  id: IntegrationProvider;
  displayName: string;
  vendor: string;
  category: 'DevTools' | 'Productivity' | 'AI' | 'Cloud' | 'Design' | 'Security' | 'Communications' | 'Other';
  kind: IntegrationKind;
  hasApi: boolean;
  tokenHelpUrl?: string;
  description: string;
  defaultCostPerSeat: number;
  /** If true, supports multi-tier mapping */
  supportsTiers?: boolean;
}

export const INTEGRATION_PROVIDERS: Record<IntegrationProvider, IntegrationProviderDef> = {
  // ─── Seat-based ────────────────────────────────────────────────────────────
  AzureDevOps: {
    id: 'AzureDevOps',
    displayName: 'Azure DevOps',
    vendor: 'Microsoft',
    category: 'DevTools',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://dev.azure.com/_usersSettings/tokens',
    description: 'Sync users + license tier (Basic / Basic+Test / Visual Studio / Stakeholder).',
    defaultCostPerSeat: 5.20,
    supportsTiers: true,
  },
  GitHub: {
    id: 'GitHub',
    displayName: 'GitHub Copilot',
    vendor: 'GitHub',
    category: 'AI',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://github.com/settings/tokens',
    description: 'GitHub Copilot seat assignments from your org.',
    defaultCostPerSeat: 15.00,
  },
  Figma: {
    id: 'Figma',
    displayName: 'Figma',
    vendor: 'Figma',
    category: 'Design',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://www.figma.com/developers/api#access-tokens',
    description: 'Team members with permission level (Viewer, Editor, Admin).',
    defaultCostPerSeat: 11.00,
    supportsTiers: true,
  },
  Claude: {
    id: 'Claude',
    displayName: 'Claude (Anthropic)',
    vendor: 'Anthropic',
    category: 'AI',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://console.anthropic.com/settings/admin-keys',
    description: 'Team members via Admin API (roles: admin, developer, billing).',
    defaultCostPerSeat: 16.00,
    supportsTiers: true,
  },
  Snyk: {
    id: 'Snyk',
    displayName: 'Snyk',
    vendor: 'Snyk',
    category: 'Security',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://app.snyk.io/account',
    description: 'Pull Snyk organization members.',
    defaultCostPerSeat: 25.00,
  },
  SendGrid: {
    id: 'SendGrid',
    displayName: 'SendGrid',
    vendor: 'Twilio',
    category: 'Communications',
    hasApi: true,
    kind: 'seat',
    tokenHelpUrl: 'https://app.sendgrid.com/settings/api_keys',
    description: 'Teammates with admin/user roles.',
    defaultCostPerSeat: 0,
  },
  Slack: {
    id: 'Slack',
    displayName: 'Slack',
    vendor: 'Slack',
    category: 'Productivity',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://api.slack.com/apps',
    description: 'Workspace members via Web API.',
    defaultCostPerSeat: 7.25,
  },
  Linear: {
    id: 'Linear',
    displayName: 'Linear',
    vendor: 'Linear',
    category: 'Productivity',
    kind: 'seat',
    hasApi: true,
    tokenHelpUrl: 'https://linear.app/settings/api',
    description: 'Workspace members via GraphQL.',
    defaultCostPerSeat: 8.00,
  },
  Notion: {
    id: 'Notion',
    displayName: 'Notion',
    vendor: 'Notion',
    category: 'Productivity',
    kind: 'seat',
    hasApi: false,
    description: 'Upload workspace members CSV (export from Notion admin).',
    defaultCostPerSeat: 10.00,
  },
  Windsurf: {
    id: 'Windsurf',
    displayName: 'Windsurf',
    vendor: 'Codeium',
    category: 'AI',
    kind: 'seat',
    hasApi: false,
    description: 'Upload CSV exported from Windsurf admin dashboard.',
    defaultCostPerSeat: 12.00,
  },
  CSV: {
    id: 'CSV',
    displayName: 'CSV Upload',
    vendor: 'Any',
    category: 'Other',
    kind: 'seat',
    hasApi: false,
    description: 'Upload a CSV with email/name columns for any tool.',
    defaultCostPerSeat: 0,
  },
  // ─── Usage-based (cloud spend) ─────────────────────────────────────────────
  AzureUsage: {
    id: 'AzureUsage',
    displayName: 'Azure Consumption',
    vendor: 'Microsoft',
    category: 'Cloud',
    kind: 'usage',
    hasApi: true,
    tokenHelpUrl: 'https://portal.azure.com/#view/Microsoft_Azure_CostManagement/Menu',
    description: 'Monthly Azure spend via Cost Management API (requires Cost Reader role).',
    defaultCostPerSeat: 0,
  },
  GCPUsage: {
    id: 'GCPUsage',
    displayName: 'Google Cloud Billing',
    vendor: 'Google',
    category: 'Cloud',
    kind: 'usage',
    hasApi: false,
    description: 'Upload BigQuery billing export CSV (needs Cloud Billing export enabled).',
    defaultCostPerSeat: 0,
  },
  TwilioUsage: {
    id: 'TwilioUsage',
    displayName: 'Twilio Usage',
    vendor: 'Twilio',
    category: 'Communications',
    kind: 'usage',
    hasApi: true,
    tokenHelpUrl: 'https://console.twilio.com/us1/account/keys-credentials/api-keys',
    description: 'Pull monthly Twilio account usage (SMS, voice, etc.).',
    defaultCostPerSeat: 0,
  },
};
